import React from 'react';
import { CurrentUserQuery, RoleId } from '../../services';

export interface RouteBase {
    title: string;
    navbar?: boolean;
    roleIds?: RoleId[];
}

export interface Route extends RouteBase {
    name: string;
    path: string;
    component?: React.ReactNode;
    exact?: boolean;
    isNested?: boolean;
}

export interface RouteGroup extends RouteBase {
    routes: Route[];
}

export type RouteItem = Route | RouteGroup;

export const isRouteGroup = (b: RouteItem): b is RouteGroup => {
    return (b as RouteGroup).routes !== undefined;
};

export const isRoute = (b: RouteItem): b is Route => {
    return (b as Route).path !== undefined;
};

type CurrentUser = CurrentUserQuery['currentUser'];

export function routeAuthFilter(currentUser: CurrentUser) {
    return (item: Pick<RouteItem, 'roleIds'>) => {
        const userRoleIds = currentUser.role_ids;

        // Super sees everything.
        if (userRoleIds.includes(RoleId.Super)) {
            return true;
        }

        // No role restriction → visible to any authenticated user.
        if (!item.roleIds || item.roleIds.length === 0) {
            return true;
        }

        // Super-only routes (e.g. Users) are visible to super alone.
        const superOnly =
            item.roleIds.length === 1 && item.roleIds[0] === RoleId.Super;
        if (superOnly) {
            return false;
        }

        // General (Admin) and Asistente General (Guest) are cross-domain: they
        // see every non-super route.
        if (
            userRoleIds.includes(RoleId.Admin) ||
            userRoleIds.includes(RoleId.Guest)
        ) {
            return true;
        }

        // Domain roles see only routes that list one of their roles.
        return item.roleIds.some((roleId) => userRoleIds.includes(roleId));
    };
}

export function routeNavbarFilter() {
    return (item: Pick<RouteItem, 'navbar'>) => {
        return item.navbar;
    };
}

export function routeNavbarAuthFilter(currentUser: CurrentUser) {
    return (item: RouteItem) => {
        const navbarFilter = routeNavbarFilter()(item);
        const authFilter = routeAuthFilter(currentUser)(item);
        return navbarFilter && authFilter;
    };
}
